const { createElement } = window.BAO.Utils

const CARD_SECTION = 'nosto-product-card-mini'
const NOSTO_TIMEOUT_MS = 4000

const cardCache = new Map()

class PushCartPairFallback extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['data-product-id', 'data-placement-id']
  }

  static get requiredExternalElements () {
    return ['carousel-template']
  }

  connectedCallback () {
    super.connectedCallback()

    this.fill()
  }

  async fill () {
    if (typeof window.nostojs !== 'function') {
      this.useLocalFallback()
      return
    }

    try {
      const products = await this.loadPlacement()
      const cards = (
        await Promise.all(
          products
            .filter(
              (product) =>
                !this.excludedHandles.has(this.handleFromUrl(product.url)),
            )
            .map((product) => this.renderCard(product)),
        )
      ).filter(Boolean)

      if (!this.isConnected) return

      if (!cards.length) {
        this.useLocalFallback()
        return
      }

      this.replaceWith(this.buildCarousel(cards))
    } catch (error) {
      console.error('Failed to load push cart pair fallback', error)

      if (this.isConnected) this.useLocalFallback()
    }
  }

  useLocalFallback () {
    const carousel =
      this.els.local?.element?.content.querySelector('drawer-carousel')

    if (!carousel) {
      this.remove()
      return
    }

    this.replaceWith(document.importNode(carousel, true))
  }

  loadPlacement () {
    return new Promise((resolve, reject) => {
      // nostojs queues callbacks until Nosto loads, so a blocked script would never settle
      const timeout = setTimeout(
        () => reject(new Error('Nosto did not respond')),
        NOSTO_TIMEOUT_MS,
      )

      window.nostojs((api) => {
        api
          .defaultSession()
          .setResponseMode('JSON_ORIGINAL')
          .viewProduct(this.productId)
          .setPlacements([this.placementId])
          .load()
          .then((response) => {
            clearTimeout(timeout)
            resolve(this.productsFromResponse(response))
          })
          .catch((error) => {
            clearTimeout(timeout)
            reject(error)
          })
      })
    })
  }

  productsFromResponse (response) {
    return (
      response?.campaigns?.recommendations?.[this.placementId]?.products ||
      response?.recommendations?.[this.placementId]?.products ||
      []
    )
  }

  async renderCard (product) {
    const url = this.productPath(product.url)
    if (!url) return null

    if (!cardCache.has(url)) {
      const sectionHtml = await window.BAO.sectionRenderer.fetchSingle(
        CARD_SECTION,
        url,
      )
      const card = window.BAO.sectionRenderer
        .convertToDOM(sectionHtml)
        .querySelector('.prd-CardMini')

      if (!card) return null

      cardCache.set(url, card)
    }

    return cardCache.get(url).cloneNode(true)
  }

  buildCarousel (cards) {
    const carousel = this.externalEls['carousel-template'].element.content
      .cloneNode(true)
      .querySelector('drawer-carousel')
    const title = carousel.querySelector('[data-pairs-carousel-title]')
    const slides = carousel.querySelector('[data-drawer-carousel-el="slides"]')

    if (title) title.textContent = this.dataset.title || ''

    cards.forEach((card) => {
      const slide = createElement('div', {
        class: 'psh-PairWith_Slide',
        'data-drawer-carousel-el': 'slide',
      })
      const frame = createElement('div', { class: 'psh-PairWith_Card' })

      frame.append(card)
      slide.append(frame)
      slides.append(slide)
    })

    return carousel
  }

  productPath (productUrl) {
    if (!productUrl) return ''

    let path

    try {
      path = new URL(productUrl, window.location.origin).pathname
    } catch (error) {
      return ''
    }

    const root = window.Shopify?.routes?.root || '/'

    if (!path.startsWith(root)) {
      path = `${root}${path.replace(/^\//, '')}`
    }

    return path
  }

  handleFromUrl (productUrl) {
    const parts = this.productPath(productUrl).split('/').filter(Boolean)
    const index = parts.indexOf('products')

    return index >= 0 ? decodeURIComponent(parts[index + 1] || '') : ''
  }

  get productId () {
    return this.dataset.productId
  }

  get placementId () {
    return this.dataset.placementId
  }

  get excludedHandles () {
    return new Set(
      (this.dataset.excludeHandles || '').split(',').filter(Boolean),
    )
  }
}

if (!customElements.get('push-cart-pair-fallback')) {
  customElements.define('push-cart-pair-fallback', PushCartPairFallback)
}
